import { useEffect, useState } from "react";
import {
  createApiKey,
  listApiKeys,
  listKeyGroups,
  revokeApiKey,
  type ApiKey,
} from "../apikeys/api";

// Settings section: mint / list / revoke agent API keys.  A created key's raw
// secret is shown exactly once, in a copyable banner — the server only stores a
// hash, so there's no way to recover it later.

const EXPIRY_OPTIONS: Array<{ label: string; days?: number }> = [
  { label: "Never" },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "1 year", days: 365 },
];

export function ApiKeysSection({ accessToken }: { accessToken: string }) {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [groups, setGroups] = useState<Array<{ id: string; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create form
  const [name, setName] = useState("");
  const [scope, setScope] = useState<string>("all"); // "all" | groupId
  const [expiryIdx, setExpiryIdx] = useState(0);
  const [creating, setCreating] = useState(false);

  // The freshly-minted secret, shown once.
  const [newToken, setNewToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const reload = async () => {
    setError(null);
    try {
      const [k, g] = await Promise.all([
        listApiKeys(accessToken),
        listKeyGroups(accessToken),
      ]);
      setKeys(k);
      setGroups(g);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const create = async () => {
    if (!name.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const { token } = await createApiKey(accessToken, {
        name: name.trim(),
        scopeGroupId: scope === "all" ? null : scope,
        expiresInDays: EXPIRY_OPTIONS[expiryIdx]?.days,
      });
      setNewToken(token);
      setCopied(false);
      setName("");
      setScope("all");
      setExpiryIdx(0);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id: string, label: string) => {
    if (!confirm(`Revoke "${label}"? Any agent using it will stop working.`)) {
      return;
    }
    setError(null);
    try {
      await revokeApiKey(accessToken, id);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const copyToken = async () => {
    if (!newToken) return;
    try {
      await navigator.clipboard.writeText(newToken);
      setCopied(true);
    } catch {
      // Clipboard can be blocked; the value is selectable in the box regardless.
    }
  };

  return (
    <section className="mt-6 space-y-4 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
      <div>
        <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
          API keys for AI agents
        </h2>
        <p className="mt-1 text-[13px] text-slate-500 dark:text-slate-400">
          Create a key to let an AI assistant manage your to-do list through the
          agent API. Scope it to a single task group or allow all of them.
        </p>
        <a
          href="/AGENT_API.md"
          download="AGENT_API.md"
          className="mt-2 inline-flex items-center gap-1.5 rounded border border-slate-300 bg-white px-2.5 py-1 text-[13px] font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Download API guide (AGENT_API.md)
        </a>
        <p className="mt-1 text-[12px] text-slate-400 dark:text-slate-500">
          Hand this file to your agent (Claude, etc.) so it knows how to call the
          API.
        </p>
      </div>

      {newToken && (
        <div className="rounded-md border border-emerald-300 bg-emerald-50 p-3 dark:border-emerald-800 dark:bg-emerald-950">
          <div className="text-[13px] font-medium text-emerald-800 dark:text-emerald-300">
            Key created — copy it now. You won't be able to see it again.
          </div>
          <div className="mt-2 flex items-center gap-2">
            <code className="flex-1 select-all break-all rounded border border-emerald-200 bg-white px-2 py-1 font-mono text-[12px] text-slate-800 dark:border-emerald-800 dark:bg-slate-900 dark:text-slate-100">
              {newToken}
            </code>
            <button
              type="button"
              onClick={() => void copyToken()}
              className="shrink-0 rounded bg-emerald-700 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-800"
            >
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <button
            type="button"
            onClick={() => setNewToken(null)}
            className="mt-2 text-[12px] text-emerald-700 underline dark:text-emerald-300"
          >
            Done
          </button>
        </div>
      )}

      {/* Create form */}
      <div className="grid gap-2 sm:grid-cols-[1fr_auto_auto_auto] sm:items-end">
        <div>
          <label className="block text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Name
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Claude assistant"
            className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-800"
          />
        </div>
        <div>
          <label className="block text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Scope
          </label>
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-800"
          >
            <option value="all">All task groups</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Expires
          </label>
          <select
            value={expiryIdx}
            onChange={(e) => setExpiryIdx(Number(e.target.value))}
            className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-800"
          >
            {EXPIRY_OPTIONS.map((o, i) => (
              <option key={o.label} value={i}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={() => void create()}
          disabled={creating || !name.trim()}
          className="rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-300"
        >
          {creating ? "Creating…" : "Create key"}
        </button>
      </div>

      {error && <div className="text-xs text-rose-600 dark:text-rose-400">{error}</div>}

      {/* Existing keys */}
      <div className="border-t border-slate-100 pt-3 dark:border-slate-800">
        {loading ? (
          <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>
        ) : keys.length === 0 ? (
          <div className="text-sm text-slate-500 dark:text-slate-400">No keys yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {keys.map((k) => (
              <li
                key={k.id}
                className="flex items-center justify-between gap-3 py-2"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                    {k.name}
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] text-slate-500 dark:text-slate-400">
                    <code className="font-mono">{k.tokenPrefix}…</code>
                    <span>·</span>
                    <span>
                      {k.scope.allGroups
                        ? "All groups"
                        : `Group: ${k.scope.group?.name ?? "(deleted)"}`}
                    </span>
                    <span>·</span>
                    <span>
                      {k.lastUsedAt
                        ? `Last used ${new Date(k.lastUsedAt).toLocaleDateString()}`
                        : "Never used"}
                    </span>
                    {k.expiresAt && (
                      <>
                        <span>·</span>
                        <span>
                          Expires {new Date(k.expiresAt).toLocaleDateString()}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => void revoke(k.id, k.name)}
                  className="shrink-0 rounded border border-rose-200 px-2 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50 dark:border-rose-800 dark:text-rose-400 dark:hover:bg-rose-950"
                >
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
